import ConfirmDialog from '@components/Shared/Dialog/ConfirmDialog';
import {
  formDialogFieldStyles, formDialogLabelStyles, formDialogTextStyles
} from '@components/Shared/Dialog/formDialog.styles';
import { useLocalization } from '@lib/localization';
import { Alert, Box, FormLabel, Stack, TextField, Typography } from '@mui/material';
import { useGetConfigQuery } from '@store/api/rootEndpoints';
import { type SsoAccountChange, type SsoChangeSummary } from '@store/api/ssoEndpoints';
import {
  useAllowSsoLinkMutation, usePreviewSsoManagementMutation, useSetSsoManagementMutation, useUnlinkSsoMutation,
  type User
} from '@store/api/userEndpoints';
import { ArrowLeftRightIcon, UnlinkIcon } from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';
import SsoChangeDialog from './SsoChangeDialog';

type Manager = 'sso' | 'local';

/** As many accounts as the server takes in one handover; more go in several. */
const handoverChunk = 1000;

const chunksOf = (ids: string[]): string[][] =>
  Array.from({ length: Math.ceil(ids.length / handoverChunk) }, (_, at) => ids.slice(at * handoverChunk, (at + 1) * handoverChunk));

/** The answers of several requests as one: their rows one after the other, their counts added. */
const merged = (summaries: SsoChangeSummary[]): SsoChangeSummary => ({
  changes: summaries.flatMap((summary) => summary.changes),
  unchanged: summaries.reduce((sum, summary) => sum + summary.unchanged, 0),
});

interface Handover {
  userIds: string[];
  managedBy: Manager;
  /** `undefined` until the server has said what would change. */
  changes?: SsoAccountChange[];
  unchanged?: number;
}

export interface SsoAccountActions {
  /** Hands accounts tied to an identity to single sign-on, or back to the admins, after a preview. */
  setManagement: (users: User[], managedBy: Manager) => void;
  unlink: (user: User) => void;
  allowLink: (user: User) => void;
  dialogs: ReactNode;
}

/**
 * The sysadmin's actions on how accounts stand with single sign-on, for the Users page: each
 * asks first and says what it will do to every account, from what the server answers to the
 * same request made as a dry run.
 */
export const useSsoAccountActions = ({ onDone }: { onDone?: () => void } = {}): SsoAccountActions => {
  const { t } = useLocalization();
  const passwordsOff = useGetConfigQuery().data?.sso?.passwordLogin === 'sysadmin_only';

  const [previewManagement] = usePreviewSsoManagementMutation();
  const [applyManagement] = useSetSsoManagementMutation();
  const [unlinkSso] = useUnlinkSsoMutation();
  const [allowSsoLink] = useAllowSsoLinkMutation();

  const [handover, setHandover] = useState<Handover | null>(null);
  const [unlinking, setUnlinking] = useState<User | null>(null);
  const [password, setPassword] = useState('');
  const passwordId = useId();

  const setManagement = (users: User[], managedBy: Manager) => {
    const userIds = users.filter((user) => user.ssoLinked).map((user) => user.id);

    if (userIds.length === 0) return;

    setHandover({ userIds, managedBy });

    void Promise.all(chunksOf(userIds).map((chunk) => previewManagement({ userIds: chunk, managedBy }))).then((results) => {
      // A refusal is said by the request's own message; there is nothing to preview.
      if (results.some((result) => result.error)) setHandover(null);
      else setHandover((current) => current?.userIds === userIds ? { ...current, ...merged(results.map((result) => result.data!)) } : current);
    });
  };

  // One chunk after another: each places its accounts before the next is sent. One that fails
  // stops the rest, and handing the same accounts over again finishes them.
  const confirmManagement = async () => {
    if (!handover) return false;

    for (const chunk of chunksOf(handover.userIds)) {
      if ((await applyManagement({ userIds: chunk, managedBy: handover.managedBy })).error) return false;
    }

    onDone?.();
  };

  const unlink = (user: User) => {
    setPassword('');
    setUnlinking(user);
  };

  const needsPassword = unlinking ? !unlinking.hasPassword : false;

  const dialogs = (
    <>
      <SsoChangeDialog
        open={handover !== null}
        title={t(handover?.managedBy === 'sso' ? 'User_SsoHandOver_Title' : 'User_SsoHandBack_Title')}
        icon={<ArrowLeftRightIcon size={16} />}
        confirmLabel={t(handover?.managedBy === 'sso' ? 'User_SsoHandOver' : 'User_SsoHandBack')}
        // Only handing over signs anybody out: an account handed back keeps its sessions.
        signOutLabel={handover?.managedBy === 'sso' ? (count) => t('User_SsoHandOver_AndSignOut', { count }) : undefined}
        description={t(handover?.managedBy === 'sso' ? 'User_SsoHandOver_Content' : 'User_SsoHandBack_Content')}
        changes={handover?.changes}
        unchanged={handover?.unchanged}
        onClose={() => setHandover(null)}
        onConfirm={confirmManagement}
      />
      <ConfirmDialog
        open={unlinking !== null}
        title={t('User_SsoUnlink_Title')}
        icon={<UnlinkIcon size={16} />}
        confirmLabel={t('User_SsoUnlink')}
        confirmColor="warning"
        confirmDisabled={needsPassword && password.length === 0}
        onClose={() => setUnlinking(null)}
        onConfirm={async () => {
          if (!unlinking) return false;

          const result = await unlinkSso({ id: unlinking.id, password: password || undefined });

          if (result.error) return false;

          onDone?.();
        }}
      >
        <Stack spacing={2}>
          <Typography sx={formDialogTextStyles}>{t('User_SsoUnlink_Content', [unlinking?.name ?? ''])}</Typography>
          <Box>
            <FormLabel htmlFor={passwordId} sx={formDialogLabelStyles}>
              {`${t('Common_Password')}${needsPassword ? '' : ` (${t('Common_Optional')})`}`}
            </FormLabel>
            <TextField
              id={passwordId}
              fullWidth
              size="small"
              type="password"
              autoComplete="new-password"
              helperText={t(needsPassword ? 'User_SsoUnlink_PasswordNeeded' : 'User_SsoUnlink_PasswordOptional')}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              sx={formDialogFieldStyles}
            />
          </Box>
          {passwordsOff && <Alert severity="warning">{t('User_SsoUnlink_PasswordOff')}</Alert>}
        </Stack>
      </ConfirmDialog>
    </>
  );

  return {
    setManagement,
    unlink,
    allowLink: (user) => void allowSsoLink(user.id).then((result) => { if (!result.error) onDone?.(); }),
    dialogs,
  };
};
